import { NavLink, Outlet } from 'react-router-dom';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { User, Building2, SlidersHorizontal, Palette, Shield, Menu, X } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { usePlanGate } from '../../hooks/usePlanGate';

const settingsNavItems: { label: string; to: string; icon: typeof User }[] = [
  { label: 'Account', to: '/settings/account', icon: User },
  { label: 'Organization', to: '/settings/organization', icon: Building2 },
  { label: 'Workspace', to: '/settings/workspace', icon: SlidersHorizontal },
  { label: 'Preferences', to: '/settings/preferences', icon: Palette },
];

export const SettingsLayout = () => {
  const { user, status } = useAuth();
  const { hasFeature } = usePlanGate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (status === 'loading') {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-neutral-500">Loading settings…</div>
      </div>
    );
  }

  const orgRole = (user as Record<string, unknown> | null)?.org_role;
  const isOrgAdmin = orgRole === 'admin' && hasFeature('org_admin_controls');

  // Filter nav items based on permissions
  const visibleNavItems = settingsNavItems.filter((item) => {
    if (item.to === '/settings/admin' && !isOrgAdmin) {
      return false;
    }
    return true;
  });

  // Add admin settings if user is org admin
  const navItems = isOrgAdmin
    ? [...visibleNavItems, { label: 'Admin Settings', to: '/settings/admin', icon: Shield }]
    : visibleNavItems;

  const MenuButtonIcon = mobileMenuOpen ? X : Menu;

  return (
    <div className="flex h-full min-h-0 bg-neutral-100/80">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2.5 rounded-xl bg-white border border-neutral-200 shadow-sm text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
          aria-label="Toggle settings menu"
        >
          <MenuButtonIcon className="w-5 h-5" strokeWidth={2} />
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar */}
      <aside
        className={cx(
          'w-72 shrink-0 flex flex-col z-40 bg-white border-r border-neutral-200/80 shadow-sm',
          'fixed lg:static inset-y-0 left-0 transform transition-transform duration-200 ease-out',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="px-5 py-6 border-b border-neutral-200/80">
          <h1 className="text-base font-semibold tracking-tight text-neutral-900">Settings</h1>
          <p className="text-xs text-neutral-500 mt-1">Account, organization & preferences</p>
        </div>

        <nav className="flex-1 py-3 px-2 overflow-y-auto">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/settings/account'}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  cx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                    isActive
                      ? 'bg-blue-500/10 text-blue-700 font-medium'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
                  )
                }
              >
                <IconComponent className="w-5 h-5 shrink-0" strokeWidth={2} aria-hidden />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-auto bg-white rounded-l-xl border border-neutral-200/80 border-l-0 shadow-sm">
        <Outlet />
      </main>
    </div>
  );
};
